import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  TextField,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  TablePagination
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { ITopPurchaser } from "../../store/interfaces/ITopPurchaser";
import { useGetTicketsByPurchaserEmailQuery, useSendEmailPurchaserMutation } from "../../store/api";
import { GeneratedTickets } from "../raffle/tickets/generatedTickets";
import { useIsMobileView } from "../../utils/responsive";
import { IWinner } from "../../store/interfaces/IWinner";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";
import { enqueueSnackbar } from "notistack";
import { getPrimaryColorByAccountId } from "../../utils/styleConstants";

// Recharts
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  LabelList
} from "recharts";

interface ITopPurchaserProps {
  purchasers: ITopPurchaser[];
  raffleId: string;
  limit: number;
}

type TPurchaserData = {
  name: string;
  email: string;
  phone?: string;
  ticketCount: number;
};

const formatNumber = (n: number) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(n);

export const PurchasersTable = ({ purchasers, raffleId, limit }: ITopPurchaserProps) => {
  const [emailFilter, setEmailFilter] = useState<string>("");
  const [selectedEmailForSearch, setSelectedEmailForSearch] = useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loggedInAccountId = useAppSelector(({ account }: RootState) => account.id);
  const primaryColor = getPrimaryColorByAccountId(loggedInAccountId);

  const { data: ticketsFounded, isLoading } = useGetTicketsByPurchaserEmailQuery(
    { email: selectedEmailForSearch, raffleId },
    { skip: selectedEmailForSearch === "" }
  );

  const [sendEmailByPurchaser, { data, isUninitialized }] = useSendEmailPurchaserMutation();
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);

  useEffect(() => {
    if (!isUninitialized && data) {
      enqueueSnackbar("Se ha enviado el correo con éxito!", {
        variant: "success",
        anchorOrigin: { horizontal: "right", vertical: "top" }
      });
    }
  }, [data, isUninitialized]);

  const isMobile = useIsMobileView();

  const handleViewTicketsClick = (email: string) => {
    setSelectedEmailForSearch(email);
    openDialog();
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailFilter(event.target.value.toLowerCase());
    setPage(0);
  };

  const filteredSortedData: TPurchaserData[] = useMemo(() => {
    return purchasers
      .filter((p) => (emailFilter ? p.email.toLowerCase().includes(emailFilter) : true))
      .sort((a, b) => b.ticketCount - a.ticketCount)
      .map((p) => ({
        name: p.name ?? p.email,
        email: p.email,
        phone: p.phone,
        ticketCount: p.ticketCount
      }));
  }, [purchasers, emailFilter]);

  const pagedData = useMemo(
    () =>
      filteredSortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredSortedData, page, rowsPerPage]
  );

  const handleSendTicketsByPurchaser = (purchaser: IWinner) => {
    setSendingEmail(true);
    sendEmailByPurchaser({
      accountId: loggedInAccountId,
      purchaserEmail: purchaser.email,
      purchaserName: purchaser.name,
      raffleId
    }).finally(() => setSendingEmail(false));
  };

  const openDialog = () => setIsDialogOpen(true);
  const closeDialog = () => setIsDialogOpen(false);

  // Tooltip personalizado
  const CustomTooltip = ({
    active,
    payload,
    label
  }: {
    active?: boolean;
    payload?: any[];
    label?: string;
  }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0]?.payload as TPurchaserData;
    return (
      <Card variant="outlined" style={{ padding: 12, maxWidth: 320 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {item.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {item.email}
        </Typography>
        {item.phone && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {item.phone}
          </Typography>
        )}
        <Typography variant="body2" sx={{ mt: 1 }}>
          Tickets: <b>{formatNumber(item.ticketCount)}</b>
        </Typography>
      </Card>
    );
  };

  return (
    <Box mt={2}>
      <Card variant="outlined">
        <CardContent>
          {(isLoading || sendingEmail) && (
            <Box sx={{ width: "100%", my: 4 }}>
              <Typography color="text.secondary" sx={{ mb: 1 }}>
                {sendingEmail ? "Enviando correo…" : "Cargando…"}
              </Typography>
              <LinearProgress title="Progreso" />
            </Box>
          )}

          {purchasers.length === 0 ? (
            <Typography variant="body1">No hay compradores</Typography>
          ) : (
            <>
              <Box paddingBottom={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={12} md={4}>
                    <TextField
                      label="Filtrar por email"
                      variant="outlined"
                      value={emailFilter}
                      onChange={handleEmailChange}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Box>

              {/* Gráfico de barras horizontal */}
              <Paper sx={{ width: "100%", overflow: "hidden" }}>
                <Box sx={{ px: 2, pt: 2, height: "100%" }}>
                  <Box
                    sx={{
                      width: "100%",
                      height: Math.min(80 * Math.max(pagedData.length, 1) + 80, 700)
                    }}
                  >
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={pagedData}
                        layout="vertical"
                        margin={{ top: 16, right: 24, left: 24, bottom: 16 }}
                        barCategoryGap={isMobile ? 18 : 22}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal vertical={false} />
                        <XAxis
                          type="number"
                          tickFormatter={formatNumber}
                          domain={[0, "dataMax"]}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={isMobile ? 96 : 180}
                          tick={{ fontSize: isMobile ? 11 : 13 }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar
                          dataKey="ticketCount"
                          fill={primaryColor}
                          radius={[0, 8, 8, 0]}
                          onClick={(data) => handleViewTicketsClick((data as any).email)}
                        >
                          <LabelList
                            dataKey="ticketCount"
                            position="right"
                            formatter={(v: any) => formatNumber(v)}
                            style={{ fontWeight: 600 }}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>

                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    component="div"
                    count={filteredSortedData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                  />
                </Box>
              </Paper>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dialog tickets */}
      <Dialog open={isDialogOpen} onClose={closeDialog} fullWidth maxWidth="xl" fullScreen={isMobile}>
        <DialogTitle>Tickets generados para {selectedEmailForSearch}</DialogTitle>
        <DialogContent>
          {ticketsFounded && !isLoading && (
            <GeneratedTickets generatedTickets={ticketsFounded} limit={limit} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} variant="outlined" color="primary">
            Cerrar
          </Button>
          <Button
            onClick={() => {
              const purchaser = purchasers.find(p => p.email === selectedEmailForSearch);
              if (purchaser) {
                handleSendTicketsByPurchaser({
                  email: purchaser.email,
                  name: purchaser.name ?? purchaser.email
                } as IWinner);
              }
            }}
            variant="contained"
            color="primary"
            disabled={sendingEmail}
          >
            Enviar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
